import { create } from 'zustand';
import type { FileStatus, SynthesisStage } from '@/services/synthesisService';

/**
 * ============================================================================
 * SYNTHESIS STORE - Batch Job & Document State Management
 * ============================================================================
 * 
 * This store manages both individual document synthesis states and batch job tracking.
 * 
 * MEMORY MANAGEMENT:
 * - Batch jobs are NOT automatically cleaned up
 * - Calling code MUST call removeBatchJob() when job is complete
 * - Recommended: Clean up jobs when status is 'completed', 'failed', or 'partially_completed'
 * - Recommended: Clean up jobs on component unmount if still in memory
 * 
 * TYPICAL FLOW:
 * 1. Submit batch → addBatchJob()
 * 2. Mark documents as processing → setProcessing(docId, jobId)
 * 3. Poll job status → updateBatchJob() + updateDocumentFromFileStatus()
 * 4. When complete → removeBatchJob() to clean up
 * 
 * ============================================================================
 */

// ============================================================================
// DOCUMENT STATE (Individual document tracking)
// ============================================================================

export interface DocumentSynthesisState {
  documentId: string;
  status: 'processing' | 'completed' | 'failed';
  startTime?: number; // Timestamp when processing started
  // NEW: Batch job tracking
  jobId?: string; // Link to batch job if part of batch
  stage?: SynthesisStage; // Current processing stage
  progress?: number; // Progress percentage (0-100)
}

// ============================================================================
// BATCH JOB STATE (Batch-level tracking)
// ============================================================================

export interface BatchJobState {
  jobId: string;
  caseId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'partially_completed';
  totalFiles: number;
  filesCompleted: number;
  filesFailed: number;
  progress: number; // Overall progress (0-100)
  documentIds: string[]; // Documents in this batch
  createdAt: string;
  startedAt?: string; // When job actually started processing
  completedAt?: string;
  isPolling: boolean; // Whether we're actively polling this job
}

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface SynthesisStore {
  // Existing: Document state tracking
  documentsState: Record<string, DocumentSynthesisState>;
  
  // NEW: Batch job tracking
  batchJobs: Record<string, BatchJobState>; // jobId -> job state
  documentToJobMap: Record<string, string>; // documentId -> jobId
  
  // Existing: Document state actions
  setProcessing: (documentId: string, jobId?: string) => void;
  setCompleted: (documentId: string) => void;
  setFailed: (documentId: string, error?: string) => void;
  getDocumentState: (documentId: string) => DocumentSynthesisState | null;
  clearDocumentState: (documentId: string) => void;
  isTimedOut: (documentId: string) => boolean;
  
  // NEW: Batch job actions
  addBatchJob: (job: BatchJobState) => void;
  updateBatchJob: (jobId: string, updates: Partial<BatchJobState>) => void;
  updateDocumentFromFileStatus: (documentId: string, fileStatus: FileStatus) => void;
  getBatchJob: (jobId: string) => BatchJobState | null;
  getJobForDocument: (documentId: string) => BatchJobState | null;
  removeBatchJob: (jobId: string) => void;
  setJobPolling: (jobId: string, isPolling: boolean) => void;
}

const TIMEOUT_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

export const useSynthesisStore = create<SynthesisStore>()((set, get) => ({
  // ============================================================================
  // STATE
  // ============================================================================
  
  documentsState: {},
  batchJobs: {},
  documentToJobMap: {},
  
  // ============================================================================
  // EXISTING DOCUMENT ACTIONS (Extended for batch support)
  // ============================================================================
  
  setProcessing: (documentId: string, jobId?: string) => {
    set((state) => {
      const updatedState: Partial<SynthesisStore> = {
        documentsState: {
          ...state.documentsState,
          [documentId]: {
            documentId,
            status: 'processing' as const,
            startTime: Date.now(),
            jobId, // NEW: Link to batch job
            stage: 'pending' as SynthesisStage, // NEW: Initial stage
            progress: 0 // NEW: Initial progress
          }
        }
      };
      
      // NEW: Update document-to-job mapping if jobId provided
      if (jobId) {
        updatedState.documentToJobMap = {
          ...state.documentToJobMap,
          [documentId]: jobId
        };
      }
      
      return updatedState;
    });
  },
  
  setCompleted: (documentId: string) => {
    set((state) => {
      const existingDoc = state.documentsState[documentId];
      
      // If document doesn't exist, create a minimal completed state
      if (!existingDoc) {
        console.warn(`[Store] setCompleted called on non-existent document: ${documentId}. Creating minimal state.`);
        return {
          documentsState: {
            ...state.documentsState,
            [documentId]: {
              documentId,
              status: 'completed' as const,
              stage: 'completed' as SynthesisStage,
              progress: 100
            }
          }
        };
      }
      
      // Update existing document
      return {
        documentsState: {
          ...state.documentsState,
          [documentId]: {
            ...existingDoc,
            status: 'completed' as const,
            stage: 'completed' as SynthesisStage,
            progress: 100
          }
        }
      };
    });
  },
  
  setFailed: (documentId: string, error?: string) => {
    set((state) => {
      const existingDoc = state.documentsState[documentId];
      
      // If document doesn't exist, create a minimal failed state
      if (!existingDoc) {
        console.warn(`[Store] setFailed called on non-existent document: ${documentId}. Creating minimal state.`);
        return {
          documentsState: {
            ...state.documentsState,
            [documentId]: {
              documentId,
              status: 'failed' as const,
              stage: 'failed' as SynthesisStage,
              progress: 0
            }
          }
        };
      }
      
      // Update existing document
      return {
        documentsState: {
          ...state.documentsState,
          [documentId]: {
            ...existingDoc,
            status: 'failed' as const,
            stage: 'failed' as SynthesisStage
            // Keep existing progress - shows how far it got before failing
          }
        }
      };
    });
    
    if (error) {
      console.error(`[Store] Document ${documentId} failed: ${error}`);
    }
  },
  
  getDocumentState: (documentId: string) => {
    return get().documentsState[documentId] || null;
  },
  
  clearDocumentState: (documentId: string) => {
    set((state) => {
      const newState = { ...state.documentsState };
      delete newState[documentId];
      
      // NEW: Also clean up document-to-job mapping
      const newMapping = { ...state.documentToJobMap };
      delete newMapping[documentId];
      
      return { 
        documentsState: newState,
        documentToJobMap: newMapping
      };
    });
  },
  
  isTimedOut: (documentId: string) => {
    const state = get().documentsState[documentId];
    if (!state || state.status !== 'processing' || !state.startTime) {
      return false;
    }
    const elapsed = Date.now() - state.startTime;
    return elapsed > TIMEOUT_DURATION;
  },
  
  // ============================================================================
  // NEW BATCH JOB ACTIONS
  // ============================================================================
  
  /**
   * Add a new batch job to tracking
   * Validates job data before adding to prevent corrupt state
   * 
   * IMPORTANT: Caller must call removeBatchJob() when job is complete
   * to prevent memory leaks. Jobs are NOT automatically cleaned up.
   */
  addBatchJob: (job: BatchJobState) => {
    // Validation: Check for required fields
    if (!job.jobId || job.jobId.trim() === '') {
      console.error('[Store] Cannot add batch job: jobId is required');
      return;
    }
    
    if (!job.caseId || job.caseId.trim() === '') {
      console.error('[Store] Cannot add batch job: caseId is required');
      return;
    }
    
    if (!job.documentIds || job.documentIds.length === 0) {
      console.error('[Store] Cannot add batch job: documentIds cannot be empty');
      return;
    }
    
    if (job.totalFiles !== job.documentIds.length) {
      console.warn(`[Store] Batch job totalFiles (${job.totalFiles}) doesn't match documentIds length (${job.documentIds.length})`);
    }
    
    // Validation: Check for duplicate job ID
    const existingJob = get().batchJobs[job.jobId];
    if (existingJob) {
      console.warn(`[Store] Batch job ${job.jobId} already exists. Overwriting.`);
    }
    
    set((state) => ({
      batchJobs: {
        ...state.batchJobs,
        [job.jobId]: job
      }
    }));
    
    console.log(`[Store] Added batch job: ${job.jobId} (${job.totalFiles} files)`);
  },
  
  /**
   * Update an existing batch job with partial updates
   */
  updateBatchJob: (jobId: string, updates: Partial<BatchJobState>) => {
    set((state) => {
      const existingJob = state.batchJobs[jobId];
      if (!existingJob) {
        console.warn(`[Store] Attempted to update non-existent job: ${jobId}`);
        return state;
      }
      
      return {
        batchJobs: {
          ...state.batchJobs,
          [jobId]: {
            ...existingJob,
            ...updates
          }
        }
      };
    });
  },
  
  /**
   * Update a document's state based on file status from batch job
   */
  updateDocumentFromFileStatus: (documentId: string, fileStatus: FileStatus) => {
    set((state) => {
      const existingDoc = state.documentsState[documentId];
      if (!existingDoc) {
        console.warn(`[Store] Attempted to update non-existent document: ${documentId}`);
        return state;
      }
      
      // Map file status to document status
      let docStatus: 'processing' | 'completed' | 'failed' = 'processing';
      if (fileStatus.status === 'completed') {
        docStatus = 'completed';
      } else if (fileStatus.status === 'failed') {
        docStatus = 'failed';
      }
      
      return {
        documentsState: {
          ...state.documentsState,
          [documentId]: {
            ...existingDoc,
            status: docStatus,
            stage: fileStatus.stage,
            progress: fileStatus.progress
          }
        }
      };
    });
  },
  
  /**
   * Get a batch job by ID
   */
  getBatchJob: (jobId: string) => {
    return get().batchJobs[jobId] || null;
  },
  
  /**
   * Get the batch job for a specific document
   */
  getJobForDocument: (documentId: string) => {
    const jobId = get().documentToJobMap[documentId];
    if (!jobId) return null;
    return get().batchJobs[jobId] || null;
  },
  
  /**
   * Remove a batch job (cleanup after completion)
   * 
   * IMPORTANT: Call this when job is complete to prevent memory leaks
   * - Removes job from batchJobs
   * - Cleans up document-to-job mappings
   * - Does NOT remove document states (they remain for badge display)
   * 
   * Safe to call multiple times - no-op if job doesn't exist
   */
  removeBatchJob: (jobId: string) => {
    set((state) => {
      const job = state.batchJobs[jobId];
      if (!job) {
        console.warn(`[Store] Attempted to remove non-existent job: ${jobId}`);
        return state;
      }
      
      // Remove job
      const newJobs = { ...state.batchJobs };
      delete newJobs[jobId];
      
      // Remove document mappings for this job
      const newMapping = { ...state.documentToJobMap };
      job.documentIds.forEach(docId => {
        delete newMapping[docId];
      });
      
      console.log(`[Store] Removed batch job: ${jobId} (${job.documentIds.length} documents)`);
      
      return {
        batchJobs: newJobs,
        documentToJobMap: newMapping
      };
    });
  },
  
  /**
   * Update polling status for a job
   */
  setJobPolling: (jobId: string, isPolling: boolean) => {
    set((state) => {
      const job = state.batchJobs[jobId];
      if (!job) return state;
      
      return {
        batchJobs: {
          ...state.batchJobs,
          [jobId]: {
            ...job,
            isPolling
          }
        }
      };
    });
  }
}));